import { Link } from "react-router-dom";
import {
  ArrowRight, ClipboardList, Home, Users, Brain, Sparkles,
  Compass, LineChart, GraduationCap, Target, Layers,
} from "lucide-react";
import SiteLayout from "@/components/SiteLayout";
import Reveal from "@/components/Reveal";
import ContactForm from "@/components/ContactForm";
import SEO, { continuumOrgJsonLd } from "@/components/SEO";
import TrustedReferences from "@/components/TrustedReferences";

// =====================
// The Catalyst Continuum — sourced from prompt-to-web-54
// Restyled in navy / gold / cream editorial palette.
// =====================

const STATS = [
  { n: "9", label: "Core frameworks", sub: "13 additional in the library" },
  { n: "10", label: "Levels of care", sub: "NARR I–IV + ASAM 1.0–4.0" },
  { n: "10", label: "Role playbooks", sub: "Reception → Medical" },
  { n: "172", label: "Playbook pages", sub: "Print-ready master doc" },
];

const ANCHORS = [
  "ASAM Criteria 4th Edition (2023)",
  "NARR Recovery Residence Standards",
  "SIMOR",
  "4E Cognition",
  "CHIME",
  "ROSC",
  "Feedback-Informed Treatment (FIT)",
  "Measurement-Based Care (MBC)",
  "Deliberate Practice",
];

const FRAMEWORKS = [
  {
    icon: <ClipboardList size={18} />,
    org: "American Society of Addiction Medicine",
    name: "ASAM Criteria 4th Ed.",
    body: "Determines the right level of care and when to move up or down.",
  },
  {
    icon: <Home size={18} />,
    org: "National Alliance for Recovery Residences",
    name: "NARR Standards",
    body: "Defines the four levels of recovery housing and the operational, ethical, and staffing standards for each.",
  },
  {
    icon: <Users size={18} />,
    org: "Best et al.",
    name: "SIMOR",
    body: "Reframes recovery as a social-identity transition from using-group to recovery-group.",
  },
  {
    icon: <Brain size={18} />,
    org: "Newen",
    name: "4E Cognition",
    body: "Treats mind as a brain-body-environment system, so we intervene in the body and the environment, not only in cognition.",
  },
  {
    icon: <Sparkles size={18} />,
    org: "Leamy et al.",
    name: "CHIME",
    body: "Names the five recovery processes (Connectedness, Hope, Identity, Meaning, Empowerment) that every intervention should reinforce.",
  },
  {
    icon: <Compass size={18} />,
    org: "SAMHSA",
    name: "ROSC",
    body: "Organizes services and community supports around the patient's chosen recovery pathway, across levels of care.",
  },
  {
    icon: <LineChart size={18} />,
    org: "Miller",
    name: "Feedback-Informed Treatment (FIT)",
    body: "Uses ultra-brief outcome (ORS) and alliance (SRS) measures every session to prevent deterioration and dropout.",
  },
  {
    icon: <Target size={18} />,
    org: "Fortney",
    name: "Measurement-Based Care (MBC)",
    body: "Uses standardized symptom measures (PHQ-9, GAD-7, PCL-5, AUDIT, DAST, WHODAS) to guide treatment decisions.",
  },
  {
    icon: <GraduationCap size={18} />,
    org: "Ericsson; Rousmaniere; Miller",
    name: "Deliberate Practice",
    body: "A structured method of solo and supervised practice targeting specific weak-signal skills.",
  },
];

const LOC_TABLE = [
  ["Recovery Housing", "NARR I–IV", "Peer-run to clinically supported homes", "3–18 months"],
  ["Outpatient", "1.0, 1.5, 1.7", "Office, telehealth", "3–12 months"],
  ["Intensive Outpatient / PHP", "2.1, 2.5, 2.7", "Day-treatment facility", "4–12 weeks"],
  ["Residential", "3.1, 3.3, 3.5", "24-hour non-medical residential", "30–180 days"],
  ["Medically Monitored / Withdrawal Mgmt", "3.7 (incl. 3.7-WM)", "Nurse-staffed, physician-directed", "3–21 days"],
  ["Medically Managed Inpatient", "4.0 (incl. 4.0-WM)", "Hospital-level", "3–14 days"],
];

const NON_NEGOTIABLES = [
  "ORS and SRS every session — feedback-informed treatment is the floor, not the ceiling.",
  "MBC on admission, weekly, and at every LOC transition (PHQ-9, GAD-7, one substance measure).",
  "Person-centered treatment plans in the patient's own language, mapped to CHIME domains.",
  "Warm handoffs at every LOC transition — a phone or video call between sending and receiving clinicians.",
  "Recovery capital is measured with the ARC or BARC-10 at admission, discharge, and 90 days post.",
  "Alumni engagement is a service line, not a marketing tactic.",
  "Deliberate practice for every clinician, every month — one skill, one recording, one tweak.",
  "Every staff member trained in trauma-informed care and Motivational Interviewing basics.",
  "Documentation is a clinical act — notes are written to move the patient forward.",
  "Nothing is delivered without a cultural humility and health-equity check.",
];

export default function Continuum() {
  return (
    <SiteLayout branch="continuum">
      <SEO
        title="The Catalyst Continuum · Behavioral Health Program Development"
        description="An operating system for behavioral healthcare — nine evidence-based frameworks, ten levels of care, one playbook that turns fragmented programs into a coherent recovery journey."
        path="/continuum"
        jsonLd={continuumOrgJsonLd}
      />
      {/* HERO */}
      <section
        className="grain relative overflow-hidden"
        style={{
          background: "var(--navy)",
          color: "white",
          padding: "9vw 6vw 6vw",
        }}
        data-testid="continuum-hero"
      >
        <Reveal className="max-w-5xl">
          <span className="eyebrow">CATALYST BEHAVIORAL HEALTH CONSULTING, LLC</span>
          <h1 className="font-display mt-6 text-[clamp(46px,6vw,88px)] leading-[1.02]">
            The Catalyst <em className="italic text-[var(--gold)]">Continuum.</em>
          </h1>
          <p className="text-neutral-300 text-[18px] leading-[1.75] mt-8 max-w-[820px]">
            An operating system for behavioral healthcare — nine evidence-based frameworks, ten levels
            of care, and one playbook that turns fragmented programs into a coherent recovery journey.
          </p>
        </Reveal>

        {/* Stats strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[var(--line-dark)] border border-[var(--line-dark)] mt-14 max-w-5xl" data-testid="continuum-stats">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 60}>
              <div className="bg-[var(--navy-2)] p-8 h-full">
                <div className="font-display text-[56px] leading-none text-[var(--gold)]">{s.n}</div>
                <div className="text-white text-sm font-bold tracking-wider mt-3 uppercase">{s.label}</div>
                <div className="text-neutral-400 text-xs mt-2 leading-relaxed">{s.sub}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <div className="brand-strip">
        <span>ASAM · NARR · SIMOR</span><i /><span>4E · CHIME · ROSC</span><i /><span>FIT · MBC</span><i /><span>DELIBERATE PRACTICE</span>
      </div>

      {/* PURPOSE */}
      <section id="purpose" className="section bg-[var(--cream)]">
        <div className="max-w-[1200px] mx-auto grid lg:grid-cols-[0.9fr_1.1fr] gap-16">
          <Reveal>
            <span className="eyebrow-dark">PURPOSE</span>
            <h2 className="font-display mt-5 text-[clamp(38px,4vw,60px)] leading-[1.04] text-[var(--navy)]">
              Eliminate the fragmentation <em className="italic text-[var(--gold)]">between levels of care.</em>
            </h2>
          </Reveal>
          <Reveal className="space-y-6 text-[var(--ink)] text-[17px] leading-[1.8]">
            <p>
              Most behavioral health organizations operate as a collection of programs rather than a
              continuum. Each level of care runs its own workflows, uses its own language, measures its
              own outcomes, and hands patients off with a discharge summary and a hope.
            </p>
            <p>
              The Catalyst Continuum assigns nine core evidence-based frameworks specific jobs in the
              patient's journey and specific jobs in each staff member's daily work. A patient in Level
              3.5 residential care experiences the same identity-supportive language from the BHT
              running morning group as they do from the case manager coordinating aftercare — and both
              are looking at the same PHQ-9, GAD-7, and ORS/SRS trend the therapist reviewed that
              morning.
            </p>
          </Reveal>
        </div>
      </section>

      {/* FRAMEWORK ANCHORS */}
      <section className="section bg-white">
        <div className="max-w-[1200px] mx-auto">
          <Reveal className="mb-10">
            <span className="eyebrow-dark">FRAMEWORK ANCHORS</span>
            <h2 className="font-display mt-4 text-[clamp(28px,3vw,40px)] leading-[1.1] text-[var(--navy)]">
              The nine that anchor everything.
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-0 border-t border-l border-[var(--line)]">
            {ANCHORS.map((a, i) => (
              <div
                key={a}
                className="border-r border-b border-[var(--line)] px-6 py-5 flex items-center gap-3 text-[var(--navy)] hover:bg-[var(--cream)] transition-colors"
                data-testid={`anchor-${i}`}
              >
                <div className="text-[var(--gold)] text-[10px] tracking-widest font-bold">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="font-bold text-sm">{a}</div>
              </div>
            ))}
          </div>
          <p className="text-[var(--muted)] text-sm mt-8 max-w-3xl italic leading-relaxed">
            Regulatory spine: national standards with Arizona (AHCCCS / ADHS Article 7) and Colorado
            (BHA) appendices.
          </p>
        </div>
      </section>

      {/* THE STACK IN ONE SENTENCE */}
      <section className="section bg-[var(--navy)] text-white">
        <Reveal className="max-w-4xl mx-auto text-center">
          <span className="eyebrow">THE FRAMEWORK STACK IN ONE SENTENCE</span>
          <p className="font-display text-[clamp(24px,2.8vw,38px)] leading-[1.35] mt-8 italic">
            <span className="text-[var(--gold)]">ASAM</span> decides where the patient belongs;{" "}
            <span className="text-[var(--gold)]">NARR</span> governs how the sober-living rung looks;{" "}
            <span className="text-[var(--gold)]">CHIME</span> describes what recovery is;{" "}
            <span className="text-[var(--gold)]">SIMOR</span> explains how it happens socially;{" "}
            <span className="text-[var(--gold)]">4E</span> extends where it happens;{" "}
            <span className="text-[var(--gold)]">ROSC</span> organizes what surrounds it;{" "}
            <span className="text-[var(--gold)]">MBC</span> and{" "}
            <span className="text-[var(--gold)]">FIT</span> tell us whether it is working; and{" "}
            <span className="text-[var(--gold)]">deliberate practice</span> ensures our clinicians keep
            getting better at delivering it.
          </p>
        </Reveal>
      </section>

      {/* FRAMEWORK STACK GRID */}
      <section id="frameworks" className="section bg-[var(--light)]">
        <Reveal className="max-w-3xl mb-12">
          <span className="eyebrow-dark">THE FRAMEWORK STACK</span>
          <h2 className="font-display mt-5 text-[clamp(36px,4vw,54px)] leading-[1.04] text-[var(--navy)]">
            Nine frameworks, <em className="italic text-[var(--gold)]">each with a job.</em>
          </h2>
        </Reveal>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 max-w-[1300px]" data-testid="frameworks-grid">
          {FRAMEWORKS.map((f, i) => (
            <Reveal key={f.name} delay={i * 50}>
              <article className="card-editorial h-full" data-testid={`framework-${i}`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="text-[var(--gold)]">{f.icon}</div>
                  <div className="text-[10px] tracking-widest text-[var(--muted)] font-bold">
                    {String(i + 1).padStart(2, "0")} / 09
                  </div>
                </div>
                <div className="text-[10px] tracking-widest text-[var(--muted)] font-bold mb-2 uppercase">
                  {f.org}
                </div>
                <h3 className="font-display text-2xl text-[var(--navy)] mb-3">{f.name}</h3>
                <p className="text-sm text-[var(--muted)] leading-relaxed">{f.body}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      {/* LEVELS OF CARE TABLE */}
      <section id="levels" className="section bg-white">
        <div className="max-w-[1300px] mx-auto">
          <Reveal className="max-w-3xl mb-10">
            <span className="eyebrow-dark">CONTINUUM AT A GLANCE</span>
            <h2 className="font-display mt-5 text-[clamp(36px,4vw,54px)] leading-[1.04] text-[var(--navy)]">
              Ten levels of care, <em className="italic text-[var(--gold)]">one operating system.</em>
            </h2>
          </Reveal>

          <div className="overflow-x-auto border border-[var(--line)]" data-testid="loc-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[var(--navy)] text-white">
                  {["Segment", "Levels", "Setting", "Typical LOS"].map((h) => (
                    <th key={h} className="text-left px-6 py-4 font-bold tracking-widest text-[11px] uppercase">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {LOC_TABLE.map((row, i) => (
                  <tr key={i} className={`border-t border-[var(--line)] ${i % 2 === 0 ? "bg-white" : "bg-[var(--light)]"} hover:bg-[var(--cream)] transition-colors`}>
                    <td className="px-6 py-5 font-display text-lg text-[var(--navy)]">{row[0]}</td>
                    <td className="px-6 py-5 text-[var(--gold)] font-bold text-xs tracking-widest">{row[1]}</td>
                    <td className="px-6 py-5 text-[var(--ink)]">{row[2]}</td>
                    <td className="px-6 py-5 text-[var(--muted)]">{row[3]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* NON-NEGOTIABLES */}
      <section className="section bg-[var(--cream)]">
        <Reveal className="max-w-3xl mb-12">
          <span className="eyebrow-dark">NON-NEGOTIABLES</span>
          <h2 className="font-display mt-5 text-[clamp(36px,4vw,54px)] leading-[1.04] text-[var(--navy)]">
            Regardless of level, role, or state, <em className="italic text-[var(--gold)]">these are always true.</em>
          </h2>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-x-8 gap-y-0 max-w-[1300px] mx-auto" data-testid="non-negotiables">
          {NON_NEGOTIABLES.map((item, i) => (
            <Reveal key={i} delay={i * 40}>
              <div className="flex gap-6 py-6 border-b border-[var(--line)]">
                <div className="font-display text-3xl text-[var(--gold)] leading-none min-w-[52px]">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <p className="text-[var(--ink)] leading-[1.65] text-[15px]">{item}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[var(--navy)] text-white grid lg:grid-cols-[0.9fr_1.1fr] gap-16 px-[6vw] py-[110px]">
        <Reveal>
          <span className="eyebrow">NEXT STEP</span>
          <h2 className="font-display mt-5 text-[clamp(38px,4vw,58px)] leading-[1.04]">
            Bring the continuum <em className="italic text-[var(--gold)]">into your organization.</em>
          </h2>
          <p className="text-neutral-300 leading-relaxed mt-6">
            A 20-minute discovery call maps your current state against the continuum and identifies the
            right tier. No pitch — a working conversation.
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to="/book" className="btn btn-gold" data-testid="continuum-book-btn">
              Book a Discovery Call <ArrowRight size={16} className="ml-2" />
            </Link>
            <Link to="/academy?category=Toolkits" className="btn btn-outline" data-testid="continuum-reference-card">
              Get the Reference Card <Layers size={16} className="ml-2" />
            </Link>
          </div>
          <div className="mt-10 pt-8 border-t border-[var(--line-dark)] text-neutral-400 text-xs tracking-widest">
            ☎ (623) 324-3237 · ✉ info@catalystbhconsulting.com
          </div>
        </Reveal>
        <Reveal>
          <ContactForm source="continuum" defaultInterest="Catalyst Continuum" />
        </Reveal>
      </section>

      <TrustedReferences
        title="Program development anchored in the frameworks payers and surveyors expect."
        refs={[
          { agency: "SAMHSA", label: "Treatment Improvement Protocols (TIPs) library", href: "https://www.samhsa.gov/resource-search/tip" },
          { agency: "ASAM", label: "The ASAM Criteria — levels of care & placement", href: "https://www.asam.org/asam-criteria" },
          { agency: "Joint Commission", label: "Behavioral Health accreditation standards", href: "https://www.jointcommission.org/accreditation-and-certification/health-care-settings/behavioral-health-care/" },
          { agency: "CARF", label: "Behavioral health program certification standards", href: "https://www.carf.org/programs/behavioral-health/" },
          { agency: "NIDA", label: "National Institute on Drug Abuse — evidence base", href: "https://nida.nih.gov/" },
          { agency: "HHS OIG", label: "Compliance guidance for BH providers", href: "https://oig.hhs.gov/compliance/compliance-guidance/" },
        ]}
      />
    </SiteLayout>
  );
}
